import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { motion, useMotionValue, animate } from 'motion/react';
import { Edit2, Plus, UserPlus } from 'lucide-react';
import { Card, SLabel } from './UI';
import { ConfirmDeleteModal, EditModal, TripManagementForm } from './Itinerary';
import { TripIdentitySummary } from './TripIdentitySummary';
import { Day, TripConfig, User } from '../types';
import { localDateKey } from '../utils/travelState';
import { isTripAdmin } from '../utils/tripRoles';

// "Your Trip" card blob colours — the same violet/emerald/terracotta
// palette Home's feature card uses for its moving tint streaks (see
// tintStreakColors in Home.tsx), same mix on every trip.
const TRIP_TINT_COLORS = ['#452873', '#184539', '#a3512e'];

// The "Your Trip" card — a peeking-track carousel for switching between the
// user's trips. Lives on the Home tab; owns only trip-level identity (title,
// dates, region, travellers) — not per-destination detail (Logistics tab) or
// the mode-specific countdown/travelling/post-trip content (a separate
// section HomeTab renders below this card).
export function TripSwitcherCard({
  trips, activeTripId, tripConfig, currentUser,
  onSelectTrip, onAddTrip, onDeleteTrip, onUpdateConfig,
  itineraryDays,
}: {
  trips: TripConfig[];
  activeTripId: string;
  tripConfig: TripConfig;
  currentUser: User;
  onSelectTrip: (id: string) => void;
  onAddTrip: () => void;
  onDeleteTrip: (id: string) => void;
  onUpdateConfig: (c: TripConfig) => Promise<boolean>;
  itineraryDays: Day[];
}) {
  const [managementMode, setManagementMode] = useState<'info' | 'members' | null>(null);
  const isAdmin = isTripAdmin(tripConfig, currentUser.id);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  useEffect(() => {
    setConfirmDelete(false);
  }, [managementMode]);

  // ── Track carousel: all trip cards on a shared sliding rail ─────────────
  // Sort: in-progress → upcoming → past. Skip empty/draft trips.
  // Calendar days, compared as the date strings they are. This compared the
  // current INSTANT against UTC midnight on the start and end dates, so in
  // Melbourne a trip only "began" at 10am on its first day and "ended" at 10am
  // on its last — after which the trip you were on sorted behind your
  // upcoming ones.
  const carouselToday = localDateKey();
  const getTripWeight = (t: TripConfig) => {
    if (!t.startDate || !t.endDate) return 3;
    const start = t.startDate.slice(0, 10), end = t.endDate.slice(0, 10);
    if (carouselToday >= start && carouselToday <= end) return 0;
    if (carouselToday < start) return 1;
    return 2;
  };
  const carouselTrips = (() => {
    const list = [...trips].filter(t => (t.title && t.title.trim()) || t.id === activeTripId);
    // Always include the active tripConfig as a fallback. This covers the window
    // where pendingTrip / a freshly-saved trip hasn't yet propagated into `trips`
    // (e.g. snapshot listener races, save retries) — without this the carousel
    // would render empty even though tripConfig has data.
    if (tripConfig && (tripConfig.title?.trim() || tripConfig.id === activeTripId) && !list.find(t => t.id === tripConfig.id)) {
      list.push(tripConfig);
    }
    return list.sort((a, b) => {
      const d = getTripWeight(a) - getTripWeight(b);
      return d !== 0 ? d : new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime();
    });
  })();
  // Prefer active id; fall back to tripConfig.id so the active card renders even
  // if activeTripId is briefly out of sync (e.g. just after a save).
  const currentTripIdx = (() => {
    const byActive = carouselTrips.findIndex(t => t.id === activeTripId);
    if (byActive >= 0) return byActive;
    if (tripConfig) {
      const byConfig = carouselTrips.findIndex(t => t.id === tripConfig.id);
      if (byConfig >= 0) return byConfig;
    }
    return 0;
  })();
  const prevCarouselTrip = currentTripIdx > 0 ? carouselTrips[currentTripIdx - 1] : null;
  const nextCarouselTrip = currentTripIdx < carouselTrips.length - 1 ? carouselTrips[currentTripIdx + 1] : null;

  // Track-based sliding carousel state
  const trackContainerRef = useRef<HTMLDivElement>(null);
  const [trackContainerWidth, setTrackContainerWidth] = useState(0);
  const trackX = useMotionValue(0);

  // -ml-6 pl-6 -mr-6 on the container: the element spans the full viewport, but
  // ResizeObserver/clientWidth measures the CONTENT BOX (excludes the pl-6 = 24px
  // left padding). So trackContainerWidth = viewport - 24. To get card width =
  // content-area width (= viewport - 48), we only subtract the RIGHT extension.
  const CAROUSEL_LEFT_EXTENSION = 24; // px visible peek zone on the left
  const CAROUSEL_RIGHT_EXTENSION = 24; // px visible peek zone on the right
  const CAROUSEL_GAP = 8; // visible gap between main card and peek card
  const carouselCardWidth = trackContainerWidth > 0
    ? trackContainerWidth - CAROUSEL_RIGHT_EXTENSION
    : 0;

  useEffect(() => {
    const el = trackContainerRef.current;
    if (!el) return;
    const measure = () => {
      const cs = getComputedStyle(el);
      const padL = parseFloat(cs.paddingLeft) || 0;
      const padR = parseFloat(cs.paddingRight) || 0;
      setTrackContainerWidth(el.clientWidth - padL - padR);
    };
    measure();
    const ro = new ResizeObserver(([entry]) => setTrackContainerWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Whether the trailing "Create new trip" slot (one past the last real trip)
  // is currently scrolled into the active position.
  const [showingCreateSlot, setShowingCreateSlot] = useState(false);

  // Snap track to active card whenever the active trip changes (dots, external nav)
  useEffect(() => {
    if (carouselCardWidth <= 0) return;
    const target = -currentTripIdx * (carouselCardWidth + CAROUSEL_GAP);
    animate(trackX, target, { type: 'spring', stiffness: 380, damping: 40, restDelta: 0.5 });
    setShowingCreateSlot(false); // any external trip switch cancels the create-slot view
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTripIdx, carouselCardWidth]);

  const switchToTrip = (id: string) => {
    if (id === activeTripId) return;
    onSelectTrip(id);
  };

  const handleTrackDragEnd = (
    _: unknown,
    info: { offset: { x: number; y: number }; velocity: { x: number; y: number } },
  ) => {
    const { offset, velocity } = info;
    const didSwipeLeft = offset.x < -50 || velocity.x < -250;
    const didSwipeRight = offset.x > 50 || velocity.x > 250;

    if (didSwipeLeft && nextCarouselTrip) {
      switchToTrip(nextCarouselTrip.id);
    } else if (didSwipeLeft && !nextCarouselTrip && !showingCreateSlot) {
      setShowingCreateSlot(true);
      const target = -carouselTrips.length * (carouselCardWidth + CAROUSEL_GAP);
      animate(trackX, target, { type: 'spring', stiffness: 380, damping: 40, restDelta: 0.5 });
    } else if (didSwipeRight && showingCreateSlot) {
      setShowingCreateSlot(false);
      const target = -currentTripIdx * (carouselCardWidth + CAROUSEL_GAP);
      animate(trackX, target, { type: 'spring', stiffness: 380, damping: 40, restDelta: 0.5 });
    } else if (didSwipeRight && prevCarouselTrip) {
      switchToTrip(prevCarouselTrip.id);
    } else {
      const target = -(showingCreateSlot ? carouselTrips.length : currentTripIdx) * (carouselCardWidth + CAROUSEL_GAP);
      animate(trackX, target, { type: 'spring', stiffness: 380, damping: 40, restDelta: 0.5 });
    }
  };

  return (
    <div>
      <div className="relative">
        <div
          ref={trackContainerRef}
          className="relative overflow-hidden -mr-6 -my-1 py-1 -ml-6 pl-6"
        >
          <motion.div
            className={`flex ${!managementMode && carouselTrips.length >= 1 ? 'cursor-grab active:cursor-grabbing touch-pan-y' : ''}`}
            style={{ x: trackX }}
            drag={!managementMode && carouselTrips.length >= 1 ? 'x' : false}
            dragConstraints={{
              left: carouselCardWidth > 0 ? -carouselTrips.length * (carouselCardWidth + CAROUSEL_GAP) : 0,
              right: 0,
            }}
            dragElastic={0.04}
            dragMomentum={false}
            onDragEnd={handleTrackDragEnd}
          >
            {carouselTrips.map((trip) => {
              const isActive = trip.id === activeTripId || (activeTripId === '' && trip.id === tripConfig?.id);
              const slotWidth = carouselCardWidth > 0
                ? carouselCardWidth
                : `calc(100% - ${CAROUSEL_RIGHT_EXTENSION}px)`;

              return (
                <div
                  key={trip.id}
                  style={{
                    width: slotWidth,
                    flexShrink: 0,
                    marginRight: CAROUSEL_GAP,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {isActive ? (
                    <Card
                      // Glass: a 60% gold wash over a blurred backdrop, so
                      // the page's drifting colour moves through the card
                      // rather than stopping at its edge. The blur is what
                      // makes it read as glass and not as a weak fill —
                      // without it, 60% of a near-white is barely a colour.
                      //
                      // A backdrop filter draws an edge at its own box, which
                      // is why it was wrong on the full-width section
                      // headings; here the box IS the card, rounded corners
                      // and all, so the edge is the thing you want to see.
                      //
                      // Its own drifting streaks stay hidden on a tinted tab
                      // (.card-tint, index.css) — the page is already
                      // drifting, and a card doing it too just reads as
                      // smudge.
                      className="bg-gold-soft/60 backdrop-blur-xl p-6 border border-gold/20 shadow-sm relative overflow-hidden"
                      style={{ contain: 'paint' }}
                    >
                      {/* Same blurred-blob treatment as Home's feature card,
                          down to the three-hue mix (tintStreakColors) and
                          the card-tint-wave-* animations (index.css) — same
                          positions, sizes, colours and motion. Region pill
                          and calendar icon stay plain gold
                          (TripIdentitySummary's default) rather than picking
                          up a per-trip colour. contain: paint is the same
                          Safari/WebKit fix used there: plain overflow-hidden
                          can fail to clip a blurred, animated descendant once
                          it's on its own compositing layer. */}
                      <div className="card-tint absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
                        {/* Purple/green given a tighter orbit (--wave-scale,
                            index.css) so they stay near their own top/bottom
                            corner instead of drifting into the middle text —
                            not a clipping box, which would cut a hard edge
                            through the soft blur. Orange keeps the full
                            orbit, matching Home's card. */}
                        <div
                          className="absolute top-6 left-[17%] w-40 h-24 -translate-x-1/2 rounded-full blur-xl"
                          style={{ backgroundColor: TRIP_TINT_COLORS[0], opacity: 0.38, animation: 'card-tint-wave-a 24s ease-in-out infinite', '--wave-scale': 0.4 } as CSSProperties}
                        />
                        <div
                          className="absolute bottom-6 left-1/2 w-40 h-20 -translate-x-1/2 rounded-full blur-xl"
                          style={{ backgroundColor: TRIP_TINT_COLORS[1], opacity: 0.34, animation: 'card-tint-wave-b 30s ease-in-out infinite', animationDelay: '-9s', '--wave-scale': 0.4 } as CSSProperties}
                        />
                        <div
                          className="absolute top-1/2 left-[83%] w-40 h-24 -translate-x-1/2 -translate-y-1/2 rounded-full blur-xl"
                          style={{ backgroundColor: TRIP_TINT_COLORS[2], opacity: 0.32, animation: 'card-tint-wave-c 27s ease-in-out infinite', animationDelay: '-16s' }}
                        />
                      </div>
                      <div className="relative z-10">
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-1">
                            <SLabel>Your Trip</SLabel>
                            {isAdmin && (
                              <div className="flex items-center gap-2">
                                <button
                                  disabled={savingConfig}
                                  onClick={(e) => { e.stopPropagation(); setManagementMode('info'); }}
                                  className="tap-target relative w-8 h-8 bg-app-bg border border-app-border rounded-xl flex items-center justify-center text-app-sub hover:bg-gold hover:text-white hover:border-gold transition shadow-sm cursor-pointer"
                                  aria-label="Edit trip details"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  disabled={savingConfig}
                                  onClick={(e) => { e.stopPropagation(); setManagementMode('members'); }}
                                  className="tap-target relative w-8 h-8 bg-app-bg border border-app-border rounded-xl flex items-center justify-center text-app-sub hover:bg-gold hover:text-white hover:border-gold transition shadow-sm cursor-pointer"
                                  aria-label="Manage travellers"
                                >
                                  <UserPlus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                          {/* line-clamp-2 caps a runaway title at two lines.
                              The matching min-h-[2lh] that used to sit here
                              (reserving both lines so every carousel card was
                              the same height) is gone: on a one-line title it
                              reserved a phantom second line, ~40px of blank
                              space between the title and the dates below.
                              Card height can now differ slightly between a
                              one- and two-line title. */}
                          <div className={`font-serif text-3xl font-bold tracking-tight line-clamp-2 ${!tripConfig.title ? 'text-app-sub/40' : 'text-app-text'}`}>
                            {tripConfig.title || 'New Trip'}
                          </div>
                        </div>

                        {/* Region pill + date range stay on screen in every
                            state — including while the edit/travellers
                            panels are open below, so you keep the context of
                            which trip you're editing. Only the travellers row
                            drops out while a panel is up (the panel itself
                            covers that ground). */}
                        <TripIdentitySummary
                          tripConfig={tripConfig}
                          itineraryDays={itineraryDays}
                          showTitle={false}
                          showTravellers
                        />

                      </div>
                    </Card>
                  ) : (
                    <div
                      className="flex-1 rounded-2xl bg-gradient-to-br from-[#fdfbf7] to-white border border-gold/10 shadow-sm relative overflow-hidden"
                      onClick={() => switchToTrip(trip.id)}
                      style={{ cursor: 'pointer', minHeight: 120 }}
                    />
                  )}
                </div>
              );
            })}

            {carouselTrips.length >= 1 && (
              <div
                style={{
                  width: carouselCardWidth > 0
                    ? carouselCardWidth
                    : `calc(100% - ${CAROUSEL_RIGHT_EXTENSION}px)`,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <button
                  onClick={onAddTrip}
                  className="flex-1 w-full rounded-2xl border-2 border-dashed border-app-border/50 bg-app-faint/30 text-app-sub/60 transition active:scale-[0.98] hover:border-app-sub/40 hover:text-app-sub flex flex-col items-center justify-center gap-2"
                  style={{ minHeight: 120 }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-app-sub text-white shadow-lg shadow-app-sub/20">
                    <Plus className="w-5 h-5 stroke-[3]" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-widest">Create New Trip</span>
                </button>
              </div>
            )}
          </motion.div>

          {!managementMode && (currentTripIdx > 0 || showingCreateSlot) && (
            <div
              className="absolute inset-y-0 left-0 pointer-events-none z-10"
              style={{
                width: CAROUSEL_LEFT_EXTENSION - CAROUSEL_GAP,
                background: 'linear-gradient(to left, transparent 0%, #f9f7f2 75%)',
              }}
            />
          )}
          {!managementMode && !showingCreateSlot && (
            <div
              className="absolute inset-y-0 right-0 pointer-events-none z-10"
              style={{
                width: CAROUSEL_RIGHT_EXTENSION - CAROUSEL_GAP,
                background: 'linear-gradient(to right, transparent 0%, #f9f7f2 75%)',
              }}
            />
          )}
        </div>

        {/* Shown with a single trip too: the dot says where you are and the
            + beside it is the way to add a second (owner, 2026-09-26). */}
        {carouselTrips.length >= 1 && !managementMode && (
          <div className="flex items-center justify-center gap-2 mt-4">
            {carouselTrips.map(t => (
              <button
                key={t.id}
                onClick={() => switchToTrip(t.id)}
                aria-label={`Switch to ${t.title || 'New Trip'}`}
                className={`tap-target-y relative h-1.5 rounded-full transition-all duration-200 ${
                  t.id === activeTripId && !showingCreateSlot ? 'w-6 bg-gold' : 'w-1.5 bg-app-border/60 hover:bg-gold/40'
                }`}
              />
            ))}
            <button
              onClick={onAddTrip}
              aria-label="Create new trip"
              className={`tap-target relative ml-1 w-5 h-5 rounded-full border flex items-center justify-center transition-all active:scale-95 ${
                showingCreateSlot
                  ? 'bg-gold border-gold text-white'
                  : 'border-dashed border-gold/40 text-gold bg-gold/5 hover:bg-gold/10'
              }`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Edit / travellers panels are portal modals rather than inline panels
          grown inside the carousel card: they used to stretch the card (and
          every sibling on the shared rail) to several screens tall, and on an
          iOS PWA the resulting scroll fought the carousel's own drag. */}
      {managementMode === 'info' && (
        <EditModal title="Trip details" onClose={() => setManagementMode(null)}>
          <TripManagementForm
            key={tripConfig.id}
            config={tripConfig}
            mode="info"
            currentUser={currentUser}
            onSavingChange={setSavingConfig}
            onSave={async (c) => {
              const saved = await onUpdateConfig(c);
              if (saved) setManagementMode(null);
              return saved;
            }}
            onCancel={() => setManagementMode(null)}
          />
          {isAdmin && (
            <div className="mt-6 pt-6 border-t border-app-border/20">
              <button
                disabled={savingConfig}
                onClick={() => setConfirmDelete(true)}
                className="w-full py-4 rounded-2xl border border-rose-100 bg-rose-50/30 text-rose-500 hover:bg-rose-50 transition cursor-pointer text-[11px] font-black uppercase tracking-widest"
              >
                Delete Trip
              </button>
            </div>
          )}
        </EditModal>
      )}

      {managementMode === 'members' && (
        <EditModal title="Who's travelling with you?" onClose={() => setManagementMode(null)}>
          <TripManagementForm
            key={tripConfig.id}
            config={tripConfig}
            mode="members"
            currentUser={currentUser}
            onSavingChange={setSavingConfig}
            onSave={async (c) => {
              const saved = await onUpdateConfig(c);
              if (saved) setManagementMode(null);
              return saved;
            }}
            onCancel={() => setManagementMode(null)}
          />
        </EditModal>
      )}

      {/* The two-stage inline button this replaces put the point of no return
          one tap from "Delete Trip", inside the form — every other delete in
          the app goes through this same sub-portal confirm. */}
      {confirmDelete && (
        <ConfirmDeleteModal
          title="Delete this trip?"
          message={`"${tripConfig.title || 'This trip'}" will be permanently deleted for everyone travelling on it, along with its itinerary and expenses.`}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            onDeleteTrip(tripConfig.id);
            setConfirmDelete(false);
            setManagementMode(null);
          }}
        />
      )}
    </div>
  );
}
